import { useEffect, useMemo, useState } from "react";
import ModalOverlay from "../../components/UI/ModalOverlay/ModalOverlay.tsx";
import styles from "./ApplyExternalJobOffer.module.scss";
import {
  X,
  ExternalLink,
  AlertCircle,
  FileText,
  Mail,
  User,
  Briefcase,
  Send,
} from "lucide-react";
import type { WorkOffer } from "./WorkApplyModal.tsx";
import { useDispatch, useSelector } from "react-redux";
import { sendWorkOfferPostulation } from "../../store/actions";
import axios from "../../axios";
import JobApplicationCvField from "../../components/WorkBag/JobApplicationCvField/JobApplicationCvField.tsx";
import { getCoverLetterValidationError } from "../../utils/jobboard/coverLetter.ts";
import { userHasMatchingOfferSkill } from "../../utils/jobboard/offerSkillMatch.ts";
import {
  applicationCvIsProvided,
  parseUserCvFromProfile,
  resolveApplicationCvForPostulation,
} from "../../utils/userCv.ts";

interface ApplyExternalJobOfferProps {
  workOffer?: WorkOffer | null;
  isOpen: boolean;
  onClose: () => void;
}

const ApplyExternalJobOffer: React.FC<ApplyExternalJobOfferProps> = ({
  workOffer,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch<any>();
  const userData = useSelector((state: any) => state.general?.userData);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvFileName, setCvFileName] = useState("");
  const [useProfileCv, setUseProfileCv] = useState(false);
  const [coverLetter, setCoverLetter] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [appliedAt, setAppliedAt] = useState<string | null>(null);

  const applicantEmail = useMemo(
    () => (userData?.email ?? userData?.username ?? "").trim().toLowerCase(),
    [userData]
  );
  const applicantName = useMemo(() => {
    const fullName = [userData?.name, userData?.last_name].filter(Boolean).join(" ").trim();
    return fullName || userData?.razon_social || "Usuario Edify";
  }, [userData]);
  const applicantPhone = useMemo(() => userData?.phone ?? "", [userData]);
  const profileCv = useMemo(() => parseUserCvFromProfile(userData), [userData]);
  const isAuthenticated = Boolean(userData?.id);
  const contactEmail = useMemo(
    () => (workOffer?.alternativeContact ?? workOffer?.user?.email ?? "").trim(),
    [workOffer]
  );

  useEffect(() => {
    const jobRequestId = Number(workOffer?.id);
    const authToken = localStorage.getItem("access-Token-Edify");
    if (!isOpen || !jobRequestId || Number.isNaN(jobRequestId) || !authToken) return;
    setIsCheckingStatus(true);
    axios
      .get(`/job-request/${jobRequestId}/postulation-status`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const hasApplied = res?.data?.alreadyApplied === true;
        setAlreadyApplied(hasApplied);
        setAppliedAt(res?.data?.appliedAt ?? null);
      })
      .catch(() => {
        setAlreadyApplied(false);
        setAppliedAt(null);
      })
      .finally(() => setIsCheckingStatus(false));
  }, [isOpen, workOffer?.id]);

  useEffect(() => {
    if (isOpen) {
      setUseProfileCv(Boolean(profileCv.url));
    }
  }, [isOpen, profileCv.url]);

  const handleClose = () => {
    if (isSubmitting) return;
    setCvFile(null);
    setCvFileName("");
    setUseProfileCv(Boolean(profileCv.url));
    setCoverLetter("");
    setAcceptedTerms(false);
    setFormError("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const jobRequestId = Number(workOffer?.id);
    if (!jobRequestId || Number.isNaN(jobRequestId)) {
      setFormError("No pudimos identificar la oferta. Volvé a intentar.");
      return;
    }
    if (!userHasMatchingOfferSkill(userData?.skills, workOffer?.skill)) {
      setFormError(
        "Para postular necesitás tener en tu perfil al menos uno de los rubros de esta búsqueda."
      );
      return;
    }
    if (!applicantEmail) {
      setFormError("Necesitamos un email en tu perfil para postularte.");
      return;
    }
    if (
      !applicationCvIsProvided({
        cvFile,
        useProfileCv,
        profileCvUrl: profileCv.url,
      })
    ) {
      setFormError("Adjuntá tu CV o usá el que tenés guardado en tu perfil.");
      return;
    }
    const coverLetterError = getCoverLetterValidationError(coverLetter, true);
    if (coverLetterError) {
      setFormError(coverLetterError);
      return;
    }
    if (!acceptedTerms) {
      setFormError("Necesitamos tu consentimiento para continuar con la postulación.");
      return;
    }
    if (alreadyApplied) {
      setFormError("Ya registramos una postulación tuya para esta oferta.");
      return;
    }
    setFormError("");
    setIsSubmitting(true);
    const cvToSend = resolveApplicationCvForPostulation({
      cvFile,
      useProfileCv,
      profileCvUrl: profileCv.url,
    });
    try {
      await dispatch(
        sendWorkOfferPostulation({
          jobRequestId,
          email: applicantEmail,
          name: applicantName,
          phone: applicantPhone,
          cv: cvToSend,
          coverLetter: coverLetter.trim(),
          isExternalOffer: true,
          externalContactEmail: contactEmail,
        })
      );
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleClose}>
      <div className={styles.modal}>
            <header className={styles.header}>
              <div className={styles.headerContent}>
                <div className={styles.externalBadge}>
                  <ExternalLink size={16} />
                  <span>Oferta Externa</span>
                </div>
                <h2 className={styles.title}>¿Te interesa esta oportunidad?</h2>
                <p className={styles.subtitle}>
                  Esta es una búsqueda externa. Edify hará todo lo posible para acercar tu postulación
                  a la empresa, aunque no podemos garantizar el contacto o la contratación final.
                </p>
              </div>
              <button className={styles.closeButton} onClick={handleClose} aria-label="Cerrar">
                <X size={20} />
              </button>
            </header>
            <div className={styles.body}>
              {workOffer?.description && workOffer.description.trim() !== "" && (
                <div className={styles.descriptionSection}>
                  <h3 className={styles.sectionTitle}>
                    <FileText size={18} />
                    Descripción de la oferta
                  </h3>
                  <div className={styles.descriptionContent}>
                    <p>{workOffer.description}</p>
                  </div>
                </div>
              )}
              <div className={styles.contactSection}>
                <h3 className={styles.sectionTitle}>
                  <FileText size={18} />
                  Completá tu postulación
                </h3>
                {isCheckingStatus && (
                  <div className={styles.statusBanner}>
                    Estamos validando si ya te postulaste a esta oferta.
                  </div>
                )}
                {!isCheckingStatus && alreadyApplied && (
                  <div className={styles.statusBannerApplied}>
                    Ya registramos tu postulación{appliedAt ? ` (${new Date(appliedAt).toLocaleDateString("es-AR")})` : ""}.
                  </div>
                )}
                <form className={styles.applyForm} onSubmit={handleSubmit}>
                  <div className={styles.formGroup}>
                    <label htmlFor="apply-external-cv" className={styles.inputLabel}>CV</label>
                    <JobApplicationCvField
                      inputId="apply-external-cv"
                      profileCvUrl={profileCv.url}
                      profileCvUpdatedAt={profileCv.updatedAt}
                      cvFile={cvFile}
                      cvFileName={cvFileName}
                      useProfileCv={useProfileCv}
                      onCvFileChange={(file, name) => {
                        setCvFile(file);
                        setCvFileName(name);
                        setFormError("");
                      }}
                      onUseProfileCvChange={setUseProfileCv}
                      disabled={isSubmitting}
                      showProfileSaveHint={isAuthenticated && !profileCv.url}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="coverLetter" className={styles.inputLabel}>
                      Carta de presentación (mín. 50 caracteres)
                    </label>
                    <textarea
                      id="coverLetter"
                      name="coverLetter"
                      className={styles.textarea}
                      placeholder="Contanos por qué sos un buen perfil para esta oportunidad."
                      rows={5}
                      value={coverLetter}
                      onChange={(e) => {
                        setCoverLetter(e.target.value);
                        setFormError("");
                      }}
                      disabled={isSubmitting}
                    />
                  </div>
                  <label className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => {
                        setAcceptedTerms(e.target.checked);
                        setFormError("");
                      }}
                      disabled={isSubmitting}
                    />
                    <span>
                      Acepto que Edify use mis datos y archivos para gestionar y acercar mi propuesta a
                      la empresa en el marco de la normativa aplicable (Argentina: Ley 25.326 y prácticas
                      de protección de datos).
                    </span>
                  </label>
                  {formError && <p className={styles.formError}>{formError}</p>}
                  <div className={styles.footerButtons}>
                    <button className={styles.secondaryButton} type="button" onClick={handleClose} disabled={isSubmitting}>
                      Cerrar
                    </button>
                    <button className={styles.primaryButton} type="submit" disabled={isSubmitting || isCheckingStatus || alreadyApplied}>
                      {alreadyApplied ? "Ya postulaste" : isSubmitting ? "Enviando..." : "Postularme"}
                      <Send size={16} />
                    </button>
                  </div>
                </form>
                <div className={styles.directContactSection}>
                  <div className={styles.directContactDivider}>
                    <span>o, si preferís</span>
                  </div>
                  <div className={styles.emailContainer}>
                    <div className={styles.emailInfo}>
                      <span className={styles.emailLabel}>Contacto directo de la empresa</span>
                      <span className={styles.emailAddress}>{contactEmail || "No informado"}</span>
                    </div>
                    {contactEmail && (
                      <a
                        href={`mailto:${contactEmail}?subject=Aplicación para puesto de trabajo - Encontrado en Edify`}
                        className={styles.copyButton}
                      >
                        <Mail size={16} />
                        Contactar
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.tipsSection}>
                <h3 className={styles.sectionTitle}>
                  <AlertCircle size={18} />
                  Consejos para tu aplicación
                </h3>
                <div className={styles.tipsList}>
                  <div className={styles.tip}>
                    <FileText className={styles.tipIcon} />
                    <div className={styles.tipContent}>
                      <h4 className={styles.tipTitle}>Prepará tu CV</h4>
                      <p className={styles.tipDescription}>
                        Asegurate de que tu CV esté actualizado y destaque tu experiencia en
                        construcción.
                      </p>
                    </div>
                  </div>
                  <div className={styles.tip}>
                    <User className={styles.tipIcon} />
                    <div className={styles.tipContent}>
                      <h4 className={styles.tipTitle}>Carta de presentación</h4>
                      <p className={styles.tipDescription}>
                        Incluí una breve carta explicando por qué sos el candidato ideal para este
                        puesto.
                      </p>
                    </div>
                  </div>
                  <div className={styles.tip}>
                    <Briefcase className={styles.tipIcon} />
                    <div className={styles.tipContent}>
                      <h4 className={styles.tipTitle}>Mencioná Edify</h4>
                      <p className={styles.tipDescription}>
                        Indicá que encontraste esta oportunidad a través de la plataforma de Edify.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.footer}>
                <div className={styles.disclaimer}>
                  <AlertCircle size={16} />
                  <span>
                    La decisión final siempre depende de la empresa que publicó la oferta. Edify hará
                    todo lo posible para acercar tu propuesta, sin garantizar respuesta o contratación.
                  </span>
                </div>
              </div>
            </div>
      </div>
    </ModalOverlay>
  );
};

export default ApplyExternalJobOffer;
